import { MOBILIA } from './mobilia';
import type { MobiliaDef } from './mobiliaTipos';

/** A mobília e as ferramentas de ofício por id (techspec-marceneiro-e-oficios-em-casa). */
export const MOBILIA_POR_ID: ReadonlyMap<string, MobiliaDef> = new Map(MOBILIA.map(m => [m.id, m]));

/** O alto do muro de um andar, em z: o topo de nenhuma peça passa dele (§04.2). Espelha MobiliaCatalog.AlturaDoMuro. */
export const ALTURA_DO_MURO = 20;
/** Espelha MobiliaCatalog.AcimaDaCabeca: abaixo disso a peça não fica em cima do personagem. */
export const ACIMA_DA_CABECA = 16;
export const ALTURA_DO_BAU = 4;

export const ehMobilia = (defId: string) => MOBILIA_POR_ID.has(defId);
export const mobiliaDe = (defId: string) => MOBILIA_POR_ID.get(defId) ?? null;
/** A altura em z da peça presa: mobília pelo catálogo, o resto (ícone no chão) ocupa 1. */
export const alturaDe = (defId: string) => MOBILIA_POR_ID.get(defId)?.altura ?? 1;
export const girosDe = (defId: string) => Math.max(1, Math.min(4, MOBILIA_POR_ID.get(defId)?.artes.length ?? 1));
export const oficioDe = (defId: string) => MOBILIA_POR_ID.get(defId)?.oficio ?? null;
/** O baú grátis da casa (techspec-casa-interface-e-baus §01.1). */
export const BAU_COMUM = 'mobilia-bau-comum';
export const ehBau = (defId: string | null | undefined) => !!defId && !!MOBILIA_POR_ID.get(defId)?.bau;
/** A peça é um assento (cadeira, banco, trono): tem a ChairTable da UO em algum giro (techspec-sentar). */
export const ehAssento = (defId: string | null | undefined) => !!defId && !!MOBILIA_POR_ID.get(defId)?.assento?.some(Boolean);
/** A maior base (acima do piso) em que a peça cabe sem o topo passar do muro. */
export const dzMaximo = (defId: string) => Math.max(0, ALTURA_DO_MURO - alturaDe(defId));
export const giroValido = (defId: string, giro: number) => ((giro % girosDe(defId)) + girosDe(defId)) % girosDe(defId);
/** A imagem crua de um giro da peça, para desenhar na casa. */
export const arteDoGiro = (defId: string, giro: number) => `/sprites/itens/mobilia/arte/${defId}-${giroValido(defId, giro)}.png`;

/**
 * A imagem do giro no instante `agora` (ms): as peças animadas do cliente do UO (vela, arandela, braseiro…) trocam de
 * quadro no ritmo do animdata.mul; as paradas devolvem a arte do giro.
 */
export function arteNoInstante(defId: string, giro: number, agora: number): string {
  const m = MOBILIA_POR_ID.get(defId);
  const g = giroValido(defId, giro);
  const n = m?.quadros?.[g] ?? 0;
  if (n < 2) return arteDoGiro(defId, giro);
  const q = Math.floor(agora / Math.max(16, m?.passoMs ?? 150)) % n;
  return `/sprites/itens/mobilia/arte/${defId}-${g}-q${q}.png`;
}

/** As ferramentas de ofício: o ofício, o def e o título do NPC que vende (espelha MobiliaCatalog.Ferramentas). */
export const FERRAMENTAS: readonly { oficio: string; defId: string; titulo: string }[] = [
  { oficio: 'ferraria', defId: 'oficio-bigorna', titulo: 'npc.titulo.ferreiro' },
  { oficio: 'alfaiataria', defId: 'oficio-roca', titulo: 'npc.titulo.alfaiate' },
  { oficio: 'arcos', defId: 'oficio-alvo', titulo: 'npc.titulo.arqueiro' },
  { oficio: 'treino', defId: 'oficio-punguista', titulo: 'npc.titulo.armeiro' }
];

/** As faixas de altura [z, z + altura) de duas peças no mesmo lugar se cruzam? */
export const cruzam = (z1: number, a1: number, z2: number, a2: number) =>
  z1 < z2 + Math.max(1, a2) && z2 < z1 + Math.max(1, a1);
